import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { rootRouteRef } from '../routes';
import { dismissUploadJob, UploadJob, useUploadJobs } from './documents/uploadJobs';
import { useToneStyles } from './ui/tones';

const useStyles = makeStyles({
  stack: {
    position: 'fixed',
    right: 'var(--modern-spacing-4, 16px)',
    bottom: 'var(--modern-spacing-4, 16px)',
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--modern-spacing-2, 8px)',
    width: 340,
    maxWidth: 'calc(100vw - 32px)',
  },
  card: {
    padding: 'var(--modern-spacing-3, 12px) var(--modern-spacing-4, 16px)',
    border: '1px solid var(--bui-border-2)',
    borderRadius: 'var(--modern-radius-md, 8px)',
    background: 'var(--bui-bg-neutral-1)',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.25)',
    fontSize: 'var(--modern-font-size-sm, 13px)',
    color: 'var(--bui-fg-secondary)',
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--modern-spacing-2, 8px)',
  },
  title: {
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  line: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 'var(--modern-spacing-2, 8px)',
    margin: 'var(--modern-spacing-1, 4px) 0',
  },
  link: {
    fontWeight: 600,
    color: 'var(--modern-info, #009DE0)',
  },
});

const JobCard = ({ job, href }: { job: UploadJob; href: string }) => {
  const classes = useStyles();
  const tones = useToneStyles();
  const { progress, summary } = job;
  const open = (
    <RouterLink to={href} className={classes.link}>
      Abrir
    </RouterLink>
  );

  if (progress) {
    const percent = Math.floor(((progress.done + progress.partial) / Math.max(progress.total, 1)) * 100);
    return (
      <div className={classes.card} role="status">
        <div className={classes.head}>
          <span className={classes.title}>Enviando pacote · {job.title}</span>
          {open}
        </div>
        <div className={classes.line}>
          <span>
            {progress.done} de {progress.total} arquivos
          </span>
          <span>{percent}%</span>
        </div>
        <LinearProgress variant="determinate" value={percent} />
      </div>
    );
  }

  const failed = summary?.failed.length ?? 0;
  return (
    <div className={classes.card} role="status">
      <div className={classes.head}>
        <span className={classes.title}>Envio concluído · {job.title}</span>
        <IconButton size="small" aria-label="Fechar" onClick={() => dismissUploadJob(job.appId)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      {summary && (
        <div className={classes.line}>
          <span>
            {summary.created} novos · {summary.versioned} alterados · {summary.unchanged} sem
            alteração
            {failed > 0 && (
              <>
                {' · '}
                <span className={tones.rejectedText}>
                  {failed} {failed === 1 ? 'falha' : 'falhas'}
                </span>
              </>
            )}
          </span>
          {open}
        </div>
      )}
    </div>
  );
};

/**
 * On every page: the package publishes going on, and how each ended until
 * dismissed. Closing or reloading the browser tab stops a publish, so it asks first.
 */
export const UploadIndicator = () => {
  const classes = useStyles();
  const jobs = useUploadJobs();
  const root = useRouteRef(rootRouteRef);
  const running = jobs.some(job => job.progress);

  useEffect(() => {
    if (!running) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  if (jobs.length === 0) {
    return null;
  }
  return (
    <div className={classes.stack}>
      {jobs.map(job => (
        <JobCard key={job.appId} job={job} href={`${root?.() ?? '/ad-ams'}/${job.appId}`} />
      ))}
    </div>
  );
};
